import { useCallback, useEffect, useState } from "react";
import type { ChecklistComponent, ChecklistTemplate, ComponentOption } from "@/types/components";

interface PricingTestCase {
  component_id: number;
  test_value: any;
  test_quantity?: number;
  expected_result?: number;
}

interface UsePricingConfiguratorStateProps {
  templateData: ChecklistTemplate | null | undefined;
}

export function usePricingConfiguratorState({ templateData }: UsePricingConfiguratorStateProps) {
  const [template, setTemplate] = useState<ChecklistTemplate | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<ChecklistComponent | null>(null);
  const [testCases, setTestCases] = useState<PricingTestCase[]>([]);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewResults, setPreviewResults] = useState<any[]>([]);

  // Load template data and initialize test cases
  useEffect(() => {
    if (templateData) {
      setTemplate(templateData);
      // Initialize test cases for each component
      const checklists = templateData.template_data?.checklists || [];
      const initialTestCases: PricingTestCase[] = [];

      checklists.forEach((checklist: any) => {
        checklist.components?.forEach((component: ChecklistComponent) => {
          initialTestCases.push({
            component_id: component.id || 0,
            test_value: getDefaultTestValue(component),
            test_quantity: 1,
            expected_result: component.base_price || 0,
          });
        });
      });

      setTestCases(initialTestCases);
    }
  }, [templateData]);

  const getDefaultTestValue = (component: ChecklistComponent): any => {
    switch (component.component_type) {
      case "text":
      case "textarea":
        return "Sample text";
      case "number":
        return 10;
      case "select":
      case "radio":
        return component.options?.[0]?.option_value || "";
      case "checkbox":
        return component.options?.slice(0, 2).map((opt) => opt.option_value) || [];
      default:
        return "";
    }
  };

  const getAllComponents = useCallback((): ChecklistComponent[] => {
    if (!template?.template_data?.checklists) return [];

    const components: ChecklistComponent[] = [];
    template.template_data.checklists.forEach((checklist: any) => {
      if (checklist.components) {
        components.push(...checklist.components);
      }
    });

    return components;
  }, [template]);

  const updateComponentPricing = useCallback(
    (componentId: number, updates: Partial<ChecklistComponent>) => {
      if (!template?.template_data?.checklists) return;

      const updatedTemplate = { ...template };
      updatedTemplate.template_data.checklists = updatedTemplate.template_data.checklists.map(
        (checklist: any) => ({
          ...checklist,
          components:
            checklist.components?.map((comp: ChecklistComponent) =>
              (comp.id || 0) === componentId ? { ...comp, ...updates } : comp
            ) || [],
        })
      );

      setTemplate(updatedTemplate);
    },
    [template]
  );

  const addComponentOption = useCallback(
    (componentId: number) => {
      const newOption: ComponentOption = {
        option_value: "new_option",
        option_label: "New Option",
        price_modifier: 0,
        price_modifier_type: "add",
        is_default: false,
        display_order: 1,
      };

      updateComponentPricing(componentId, {
        options: [...(selectedComponent?.options || []), newOption],
      });

      if (selectedComponent?.id === componentId) {
        setSelectedComponent((prev) =>
          prev
            ? {
                ...prev,
                options: [...(prev.options || []), newOption],
              }
            : null
        );
      }
    },
    [selectedComponent, updateComponentPricing]
  );

  const updateComponentOption = useCallback(
    (componentId: number, optionIndex: number, updates: Partial<ComponentOption>) => {
      if (!selectedComponent?.options) return;

      const updatedOptions = [...selectedComponent.options];
      updatedOptions[optionIndex] = { ...updatedOptions[optionIndex], ...updates };

      updateComponentPricing(componentId, { options: updatedOptions });
      setSelectedComponent((prev) => (prev ? { ...prev, options: updatedOptions } : null));
    },
    [selectedComponent, updateComponentPricing]
  );

  const removeComponentOption = useCallback(
    (componentId: number, optionIndex: number) => {
      if (!selectedComponent?.options) return;

      const updatedOptions = [...selectedComponent.options];
      updatedOptions.splice(optionIndex, 1);

      updateComponentPricing(componentId, { options: updatedOptions });
      setSelectedComponent((prev) => (prev ? { ...prev, options: updatedOptions } : null));
    },
    [selectedComponent, updateComponentPricing]
  );

  return {
    // State
    template,
    selectedComponent,
    testCases,
    showPreviewDialog,
    previewResults,

    // Setters
    setTemplate,
    setSelectedComponent,
    setTestCases,
    setShowPreviewDialog,
    setPreviewResults,

    // Helpers
    getAllComponents,
    updateComponentPricing,
    addComponentOption,
    updateComponentOption,
    removeComponentOption,
  };
}
